// ============================================================
// detectStreamType — fetch master playlist and detect LL-HLS
//
// GoodGame LL-HLS uses demuxed audio/video (VERSION:6):
//   master → #EXT-X-MEDIA:TYPE=AUDIO + separate -audio.m3u8 / -video.m3u8
// Standard HLS uses muxed MPEG-TS (VERSION:3):
//   master → no #EXT-X-MEDIA, single _quality.m3u8
//
// Detection: parse master playlist ONLY (no child fetches needed).
// Check for demuxed audio renditions or LL-HLS specific tags.
// ============================================================

/** LL-HLS tags that may appear in any playlist */
const LL_HLS_TAGS = [
  '#EXT-X-PART-INF',
  '#EXT-X-SERVER-CONTROL',
  '#EXT-X-PART:',
  '#EXT-X-PRELOAD-HINT',
];

/**
 * Detect whether a stream is LL-HLS by inspecting the master playlist.
 *
 * Detection (from master only — no child playlist fetches):
 * 1. If any LL-HLS tags are found directly in the playlist → LL-HLS
 * 2. If the master has demuxed audio renditions
 *    (#EXT-X-MEDIA:TYPE=AUDIO with URI=) → LL-HLS
 *    (GoodGame LL-HLS uses demuxed fMP4; standard HLS uses muxed MPEG-TS)
 *
 * Falls back to `false` on any error (standard HLS as safe default).
 */
export async function detectStreamType(playlistUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const resp = await fetch(playlistUrl, {
      signal: controller.signal,
      cache: 'no-cache',
    });
    clearTimeout(timeout);

    if (!resp.ok) return false;

    const text = await resp.text();
    return isLowLatencyMaster(text);
  } catch {
    // Network error, CORS block, timeout → default to standard HLS
    return false;
  }
}

/**
 * Check if a master playlist indicates LL-HLS.
 * Exported for unit testing.
 */
export function isLowLatencyMaster(masterText: string): boolean {
  // 1. Direct LL-HLS tags (if master IS also a media playlist)
  if (LL_HLS_TAGS.some(tag => masterText.includes(tag))) return true;

  // 2. Demuxed audio renditions — GoodGame LL-HLS indicator
  //    #EXT-X-MEDIA:TYPE=AUDIO,...,URI="...something..."
  if (/^#EXT-X-MEDIA:[^\n]*TYPE=AUDIO[^\n]*URI="/m.test(masterText)) return true;

  return false;
}
